import Swiper from 'swiper';
import SwiperCore, { Navigation, Mousewheel } from 'swiper/core';
import 'swiper/css';
import debounce from 'lodash/debounce';
SwiperCore.use([Navigation]);

export default class PdpMediaGallery {
    /**
        * Class constructor *
        * @arg {object} - `sliderElement` check for the
        * product carousel element.
    **/
    constructor (sliderElement) {
        if (!sliderElement) return;

        this.sliderElement = sliderElement;
        this.carousel;
        this.carouselThumbnails;
        this.sliderHasThumbnails = false;
        this.hasThumbnailsSlider = this.sliderElement.nextElementSibling?.firstElementChild.classList.contains('js-pdp-media-gallery-thumbnails');

        if (this.hasThumbnailsSlider) {
            this.sliderThumbnailsElement = this.sliderElement.nextElementSibling.firstElementChild;
            this.thumbnailsNavigationPrev = document.querySelector('.js-pdp-media-thumbnails-navigation-prev');
            this.thumbnailsNavigationNext = document.querySelector('.js-pdp-media-thumbnails-navigation-next');
            this.sliderHasThumbnails = true;
            this.handleDisabledArrows(null, 0);
        }

        this.init()
        this.initScrollBar()

        window.addEventListener('variantChange', (event) => {
            this.updateVariantImages(event.detail);
        })
    }

    init() {
        const breakpoint = ! this.sliderElement.classList.contains('js-pdp-media-gallery-desktop') ? window.matchMedia('(min-width: 767px)') : null;
        let carousel;
        let carouselThumbnails;
        let self = this;

        const breakpointChecker = function() {
            if (breakpoint.matches === true) {
                if (carousel !== undefined) {
                    carousel.destroy(true, true);
                }

                /**
                 * Removes `swiper` classes.
                 */
                self.sliderElement.classList.remove('swiper');
                self.sliderElement.querySelector('.js-swiper-wrapper').classList.remove('swiper-wrapper');
                self.sliderElement.querySelectorAll('.js-swiper-slide').forEach(slide => slide.classList.remove('swiper-slide'));

                if (self.sliderHasThumbnails) {
                    self.sliderThumbnailsElement.classList.remove('swiper');
                    self.sliderThumbnailsElement.querySelector('.js-swiper-wrapper').classList.remove('swiper-wrapper');
                    self.sliderThumbnailsElement.querySelectorAll('.js-swiper-slide').forEach(slide => slide.classList.remove('swiper-slide'));
                }

                return ;
            } else {
                /**
                 * Adds `swiper` classes.
                 */
                self.sliderElement.classList.add('swiper');
                self.sliderElement.querySelector('.js-swiper-wrapper').classList.add('swiper-wrapper');
                self.sliderElement.querySelectorAll('.js-swiper-slide').forEach(slide => slide.classList.add('swiper-slide'));

                if (self.sliderHasThumbnails) {
                    self.sliderThumbnailsElement.classList.add('swiper');
                    self.sliderThumbnailsElement.querySelector('.js-swiper-wrapper').classList.add('swiper-wrapper');
                    self.sliderThumbnailsElement.querySelectorAll('.js-swiper-slide').forEach(slide => slide.classList.add('swiper-slide'));
                }

                return enableSwiper();
            }
        };

        const enableSwiper = function() {
            carousel = new Swiper(self.sliderElement, {
                slidesPerView: 1,
                spaceBetween: 10,
                threshold: 10,
                centeredSlides: true,
                centeredSlidesBounds: true,
                breakpoints: {
                    768: {
                        slidesPerView: 1
                    }
                },
                on: {
                    init() {
                        if (self.sliderThumbnailsElement) {
                            carouselThumbnails = new Swiper(self.sliderThumbnailsElement, {
                                modules: [ Mousewheel ],
                                mousewheel: true,
                                slidesPerView: 5,
                                spaceBetween: 10,
                                threshold: 10,
                                on: {
                                    init() {
                                        self.sliderThumbnailsElement.querySelectorAll('.js-pdp-media-gallery-thumbnail').forEach((button, index) => {
                                            button.addEventListener('click', () => {
                                                if (button.dataset.slideIndex) {
                                                    carousel.slideTo(button.dataset.slideIndex);
                                                } else {
                                                    carousel.slideTo(index);
                                                }
                                                button.classList.add('is-active');
                                            });
                                        })
                                    }
                                }
                            });
                        }
                    },
                    slideChange(swiper) {
                        if (self.sliderThumbnailsElement) {
                            carouselThumbnails.slideTo(swiper.activeIndex);
                            self.sliderThumbnailsElement.querySelectorAll('.swiper-slide').forEach(slide => slide.querySelector('.js-pdp-media-gallery-thumbnail').classList.remove('is-active'));
                            self.sliderThumbnailsElement.querySelectorAll(`.swiper-slide button[data-slide-index="${swiper.activeIndex}"]`).forEach(btn => btn.classList.add('is-active'));
                        }
                        self.handleDisabledArrows(swiper, swiper.activeIndex);
                    }
                }
            });

            if (self.thumbnailsNavigationPrev && self.thumbnailsNavigationNext) {
                if (self.thumbnailsNavigationPrev) {
                    self.thumbnailsNavigationPrev.addEventListener('click', () => {
                        carousel.slidePrev();
                        carouselThumbnails.slidePrev();
                    });
                }
                if (self.thumbnailsNavigationNext) {
                    self.thumbnailsNavigationNext.addEventListener('click', () => {
                        carousel.slideNext();
                        carouselThumbnails.slideNext();
                    });
                }
            }

            self.carousel = carousel;
            self.carouselThumbnails = carouselThumbnails;
        };

        if (breakpoint) {
            breakpoint.addEventListener('change', breakpointChecker);
            breakpointChecker();
        } else {
            enableSwiper();
        }
    }

    updateVariantImages (variant) {
        const mediaImages = document.querySelectorAll('.js-pdp-media-gallery-variant');
        const isMobile = window.innerWidth < 768;
        if (isMobile) {
            this.carousel?.slideTo(0);
            this.carouselThumbnails?.slideTo(0);
        }
        if (mediaImages) {
            mediaImages.forEach(media => {
                const mediaVariantId = media.dataset.variantId;
                if (mediaVariantId == variant.id) {
                    media.classList.remove('hidden');
                } else {
                    media.classList.add('hidden');
                }
            })
        }
        this.sliderThumbnailsElement.querySelectorAll('[data-slide-index="0"]').forEach(slide => slide.classList.add('is-active'));
        this.updateScrollbarVisibility();
    }

    handleDisabledArrows(swiper, index) {
        if (this.hasThumbnailsSlider) {
            if (index === 0) {
                this.thumbnailsNavigationPrev.classList.add('opacity-20');
            } else {
                this.thumbnailsNavigationPrev.classList.remove('opacity-20');
            }
            if (swiper) {
                const slideLength = document.querySelectorAll('li.js-pdp-media-gallery-thumbnail-variant:not(.hidden)').length;
                if (index + 1 === slideLength) {
                    this.thumbnailsNavigationNext.classList.add('opacity-20');
                } else {
                    this.thumbnailsNavigationNext.classList.remove('opacity-20');
                }
            }
        }
    }

    initScrollBar() {
        const productMedia = document.querySelector('.js-product-media');
        const scrollWrapper = document.querySelector('.js-product-media-scroll');
        if (scrollWrapper) {
            this.productMedia = productMedia;
            this.scrollWrapper = scrollWrapper;

            const scrollBar = document.querySelector('.js-product-media-scroll-bar');
            const scrollUpBtn = scrollWrapper.querySelector('.js-product-media-scroll-up');
            const scrollDownBtn = scrollWrapper.querySelector('.js-product-media-scroll-down');

            const debouncedScrollCalcs = debounce((event) => {
                const track = scrollBar.parentElement;
                const scrollFullHeight = track.offsetHeight - scrollBar.offsetHeight;
                const scrollHeight = productMedia.scrollHeight - productMedia.clientHeight;
                const currentPercentage = (event.target.scrollTop / scrollHeight) * 100;
                const currentScrollHeight = (currentPercentage / 100) * scrollFullHeight;
                scrollBar.style.transform = `translateY(${currentScrollHeight}px)`;
            }, 10);

            productMedia.addEventListener('scroll', (event) => {
                debouncedScrollCalcs(event);
            });

            scrollUpBtn?.addEventListener('click', () => {
                productMedia.scrollBy({ top: -(productMedia.clientHeight / 2), behavior: 'smooth' });
            });

            scrollDownBtn?.addEventListener('click', () => {
                productMedia.scrollBy({ top: productMedia.clientHeight / 2, behavior: 'smooth' });
            });

            let isDragging = false;
            let dragStartY = 0;
            let dragStartScrollTop = 0;

            scrollBar.addEventListener('mousedown', (e) => {
                isDragging = true;
                dragStartY = e.clientY;
                dragStartScrollTop = productMedia.scrollTop;
                document.body.style.cursor = 'grabbing';
                document.body.style.userSelect = 'none';
                e.preventDefault();
            });

            document.addEventListener('mousemove', (e) => {
                if (!isDragging) return;
                const track = scrollBar.parentElement;
                const scrollFullHeight = track.offsetHeight - scrollBar.offsetHeight;
                const maxScrollTop = productMedia.scrollHeight - productMedia.clientHeight;
                const deltaY = e.clientY - dragStartY;
                const scrollDelta = (deltaY / scrollFullHeight) * maxScrollTop;
                productMedia.scrollTop = Math.max(0, Math.min(maxScrollTop, dragStartScrollTop + scrollDelta));
            });

            document.addEventListener('mouseup', () => {
                if (!isDragging) return;
                isDragging = false;
                document.body.style.cursor = '';
                document.body.style.userSelect = '';
            });

            window.addEventListener('load', () => {
                this.updateScrollbarVisibility();
            });

            window.addEventListener('resize', debounce(() => {
                this.updateScrollbarVisibility();
            }, 200));
        }
    }

    updateScrollbarVisibility() {
        if (!this.scrollWrapper || !this.productMedia) return;

        const hasOverflow = this.productMedia.scrollHeight > this.productMedia.clientHeight;
        this.scrollWrapper.classList.toggle('opacity-0', !hasOverflow);
        this.scrollWrapper.classList.toggle('pointer-events-none', !hasOverflow);
    }
}
